import { defineBrowserModule } from "@amtana/browser";
import type { BrowserModule, NavigationEntry, ScreenDeclaration } from "@amtana/browser";
import { phrase } from "@amtana/i18n/catalog";
import type { PlainKey } from "@amtana/i18n/catalog";

import { MAIL_ERROR_COPY } from "./model/mail-error-copy.ts";
import { OPS_CATALOGS } from "./model/ops-copy.ts";
import type { OpsCatalog } from "./model/ops-copy.ts";

// The back office (epic 09, epic 12, ADR-005): inside the platform's own frame, the way
// Settings is (S12.02). Staff find it in the main sidebar just above Settings (S12.01); its own
// sidebar groups Overview; Tenants; Platform; Operations. Every screen and entry requires
// `ops:read`, so for anyone else every /ops page is the platform's ordinary not-found screen and
// no entry, sidebar or quick-search page names it (`requiresPermission`, @amtana/browser).

const STAFF = "ops:read";

const opsPhrase = (key: PlainKey<OpsCatalog>) => phrase(OPS_CATALOGS, key);

const screen = (
  path: string,
  title: PlainKey<OpsCatalog>,
  load: ScreenDeclaration["load"],
): ScreenDeclaration => ({
  path,
  title: opsPhrase(title),
  layout: "app",
  requiresPermission: STAFF,
  load,
});

const entry = (fields: Omit<NavigationEntry, "requiresPermission">): NavigationEntry => ({
  ...fields,
  requiresPermission: STAFF,
});

export const opsBrowserModule: BrowserModule = defineBrowserModule("ops")
  .withScreens({
    overview: screen("/ops", "titleOverview", () => import("./ui/sections/ops-overview-screen.tsx")),
    organizations: screen(
      "/ops/organizations",
      "titleOrganizations",
      () => import("./ui/sections/ops-organizations-screen.tsx"),
    ),
    organizationDetail: screen(
      "/ops/organizations/:id",
      "titleOrganization",
      () => import("./ui/sections/ops-organization-detail-screen.tsx"),
    ),
    people: screen("/ops/users", "titlePeople", () => import("./ui/sections/ops-people-screen.tsx")),
    personDetail: screen(
      "/ops/users/:id",
      "titlePerson",
      () => import("./ui/sections/ops-person-detail-screen.tsx"),
    ),
    staff: screen("/ops/staff", "titleStaff", () => import("./ui/sections/ops-staff-screen.tsx")),
    flags: screen("/ops/flags", "titleFlags", () => import("./ui/sections/ops-flags-screen.tsx")),
    providers: screen(
      "/ops/providers",
      "titleProviders",
      () => import("./ui/sections/ops-providers-screen.tsx"),
    ),
    queues: screen("/ops/queues", "titleQueues", () => import("./ui/sections/ops-queues-screen.tsx")),
    compute: screen("/ops/compute", "titleCompute", () => import("./ui/sections/ops-compute-screen.tsx")),
    slack: screen("/ops/slack", "titleSlack", () => import("./ui/sections/ops-slack-screen.tsx")),
    funnel: screen("/ops/funnel", "titleFunnel", () => import("./ui/sections/ops-funnel-screen.tsx")),
    search: screen("/ops/search", "titleSearch", () => import("./ui/sections/ops-search-screen.tsx")),
  })
  .withNavigation([
    entry({
      id: "back-office",
      group: "utilities",
      label: opsPhrase("backOffice"),
      href: "/ops",
      icon: "lock",
      order: 10,
    }),
    entry({
      id: "ops-overview",
      group: "back-office",
      label: opsPhrase("overviewTitle"),
      href: "/ops",
      icon: "dashboard",
      order: 10,
    }),
    entry({
      id: "ops-funnel",
      group: "back-office",
      label: opsPhrase("funnelPageTitle"),
      href: "/ops/funnel",
      icon: "chart",
      order: 20,
    }),
    entry({
      id: "ops-organizations",
      group: "back-office-tenants",
      label: opsPhrase("organizationsTitle"),
      href: "/ops/organizations",
      icon: "building",
      order: 10,
    }),
    entry({
      id: "ops-people",
      group: "back-office-tenants",
      label: opsPhrase("peopleTitle"),
      href: "/ops/users",
      icon: "users",
      order: 20,
    }),
    entry({
      id: "ops-flags",
      group: "back-office-platform",
      label: opsPhrase("flagsTitle"),
      href: "/ops/flags",
      icon: "filter",
      order: 10,
    }),
    entry({
      id: "ops-staff",
      group: "back-office-platform",
      label: opsPhrase("staffTitle"),
      href: "/ops/staff",
      icon: "badge-check",
      order: 20,
    }),
    entry({
      id: "ops-providers",
      group: "back-office-platform",
      label: opsPhrase("providersPageTitle"),
      href: "/ops/providers",
      icon: "puzzle",
      order: 30,
    }),
    entry({
      id: "ops-slack",
      group: "back-office-platform",
      label: opsPhrase("slackPageTitle"),
      href: "/ops/slack",
      icon: "hash",
      order: 40,
    }),
    entry({
      id: "ops-search",
      group: "back-office-operations",
      label: opsPhrase("searchPageTitle"),
      href: "/ops/search",
      icon: "search",
      order: 30,
    }),
    entry({
      id: "ops-queues",
      group: "back-office-operations",
      label: opsPhrase("queuesTitle"),
      href: "/ops/queues",
      icon: "layers",
      order: 10,
    }),
    entry({
      id: "ops-compute",
      group: "back-office-operations",
      label: opsPhrase("computeTitle"),
      href: "/ops/compute",
      icon: "server",
      order: 20,
    }),
  ])
  .withErrorCopy({
    ops_last_manager: {
      title: opsPhrase("lastManagerTitle"),
      description: opsPhrase("lastManagerDescription"),
    },
    ops_dead_letter_not_found: {
      title: opsPhrase("deadLetterGoneTitle"),
      description: opsPhrase("deadLetterGoneDescription"),
    },
    // Mail has no browser half: its codes reach a person only through this back office's
    // Providers page (ADR-008), so the copy lives here.
    ...MAIL_ERROR_COPY,
  });
